import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import useApiService from '../../component/hooks/useApiService';
import { fetchOfferRequests } from '../../services/offerRequests';

const STATUS_LABELS = {
  pending: 'Beklemede',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  completed: 'Tamamlandı',
};

const renderStatusLabel = (status) => STATUS_LABELS[status] || status || '-';

const normalizeRow = (raw, idx) => {
  const boosts = raw?.boost_codes || raw?.boosts || raw?.requested_boosts || [];
  const boostCodes = Array.isArray(boosts)
    ? boosts.map((b) => (typeof b === 'string' ? b : b?.code || b?.category || b?.name)).filter(Boolean)
    : [];

  const preferredDate =
    raw?.expected_investment_date ||
    raw?.expected_date ||
    raw?.preferred_date ||
    raw?.investment_date ||
    raw?.created_at ||
    raw?.createdAt;

  return {
    id: raw?.id || raw?.request_id || raw?.requestId || raw?.ticket_id || idx,
    userEmail: raw?.user_email || raw?.email || raw?.client_email || raw?.clientEmail || '',
    advisorEmail: raw?.advisor_email || raw?.admin_email || raw?.advisorEmail || '',
    amountUsd: raw?.investment_amount_usd || raw?.amount_usd || raw?.amount || raw?.investment_amount || null,
    preferredDate,
    boosts: boostCodes,
    status: raw?.status || 'pending',
    raw,
  };
};

const OfferRequestList = () => {
  const { executeGet, extractApiError } = useApiService();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({ advisor: '', user: '' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      load();
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchOfferRequests(executeGet, {
        advisor_email: filters.advisor.trim() || undefined,
        user_email: filters.user.trim() || undefined,
        limit: 200,
        offset: 0,
      });
      const rawItems =
        (Array.isArray(res?.items) && res.items) ||
        (Array.isArray(res?.data?.items) && res.data.items) ||
        (Array.isArray(res?.data) && res.data) ||
        (Array.isArray(res) && res) ||
        [];
      setRows(rawItems.map(normalizeRow));
    } catch (e) {
      setError(extractApiError ? extractApiError(e) : e?.message || 'Veriler alınamadı.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasFilters = useMemo(() => Boolean(filters.advisor || filters.user), [filters]);

  return (
    <Box p={3}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Teklif Talepleri
      </Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Müşteri e-posta"
            value={filters.user}
            onChange={(e) => setFilters((prev) => ({ ...prev, user: e.target.value }))}
            size="small"
            onKeyDown={handleKeyDown}
          />
          <TextField
            label="Danışman e-posta"
            value={filters.advisor}
            onChange={(e) => setFilters((prev) => ({ ...prev, advisor: e.target.value }))}
            size="small"
            onKeyDown={handleKeyDown}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={load}
              disabled={loading}
            >
              Filtrele
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setFilters({ advisor: '', user: '' });
                setTimeout(load, 0);
              }}
              disabled={loading && !hasFilters}
            >
              Sıfırla
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Müşteri</TableCell>
                <TableCell>Danışman</TableCell>
                <TableCell>Yatırım (USD)</TableCell>
                <TableCell>Hedef Tarih</TableCell>
                <TableCell>Boostlar</TableCell>
                <TableCell>Durum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Kayıt bulunamadı.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/offer-requests/${row.id}`)}
                  >
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.userEmail || '-'}</TableCell>
                    <TableCell>{row.advisorEmail || '-'}</TableCell>
                    <TableCell>{row.amountUsd ? `$${row.amountUsd}` : '-'}</TableCell>
                    <TableCell>{row.preferredDate ? new Date(row.preferredDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      {row.boosts?.length
                        ? row.boosts.map((code) => (
                            <Chip key={code} label={code} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={renderStatusLabel(row.status)}
                        size="small"
                        color={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default OfferRequestList;
